import { Link, usePage, router } from '@inertiajs/react'
import { Button } from "@/components/ui/button";
import { Edit, LogOut, MessageCircle } from "lucide-react";
import LinkedinIcon from "@/components/icons/LinkedinIcon";
import { SpeakerHeader } from "@/components/SpeakerHeader";
import { Footer } from "@/components/Footer";
import { generateSpeakerBioPDF } from "@/lib/speakerPdfGenerator";
import { SpeakerDetails } from "@/components/SpeakerDetails";
import { SpeakerDetailsInterface } from "@/types/SpeakerDetailsInterface";
import BackToDirectoryButton from '@/components/BackToDirectoryButton';
import { useEffect, useState } from 'react';
import { ViewConversationDialog } from '@/components/messaging/ViewConversationDialog';
import HTMLHead from '@/components/HTMLHead';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

interface SpeakerProfileProps {
  speaker: SpeakerDetailsInterface;
  existingConversationId: number | null;
  openContact: boolean;
}

const SpeakerProfile = ({speaker, existingConversationId, openContact}: SpeakerProfileProps) => {
  const { auth } = usePage().props as { auth?: { user?: { id: number; name: string; isSpeaker: boolean } } };
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Merged slide list: HQ headshots then album images
  const allPhotos = [
    ...(speaker.headshots ?? []),
    ...(speaker.albumImages ?? []),
  ];

  // If the main profile photo (subject_type: user) is not already in the gallery, prepend it
  const mainPhotoInGallery = allPhotos.some(h => h.id === speaker.image?.id);
  const lightboxSlides = [
    ...(!mainPhotoInGallery && speaker.imageUrl
      ? [{ src: speaker.imageUrl, alt: speaker.name }]
      : []),
    ...allPhotos.map(h => ({ src: h.imageUrl, alt: h.caption })),
  ];

  // Find the main profile photo index in the lightbox slides
  const mainPhotoIndex = mainPhotoInGallery
    ? allPhotos.findIndex(h => h.id === speaker.image?.id)
    : 0;

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Scroll to top when component mounts or id changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [speaker.id]);

  const downloadBioPDF = () => {
    if (!speaker) return;
    const speakerForPdf = {
      ...speaker};
    generateSpeakerBioPDF(speakerForPdf);
  };

  const shareOnLinkedIn = () => {
    if (!speaker) return;
    
    const currentUrl = window.location.href;
    const shareText = `Check out ${speaker.name}, ${speaker.title} – an amazing cybersecurity professional available for speaking engagements! 🚀 #CyberSecurity #WomenInTech #SheSpeaksCyber`;
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(shareText)}`;
    
    window.open(linkedInUrl, '_blank', 'noopener,noreferrer');
  };

  return (
<>
    <HTMLHead title={speaker?.name} description={speaker?.bio ?? null} image={speaker?.imageUrl ?? speaker?.avatarUrl} />
    
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <div className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-2">
             <BackToDirectoryButton></BackToDirectoryButton>
            <div className="flex items-center gap-2">
              {auth?.user?.id === speaker.id ? (
                <div className="flex items-center gap-2">
                  <Button variant="default" asChild>
                    <Link href={`/profile/edit`}>
                        <Edit className="w-4 h-4" />
                        <span className="hidden sm:inline">Edit Profile</span>
                    </Link>
                </Button>
                <Button
                    variant="ghost"
                    onClick={() => router.post('/logout')}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden sm:inline">Logout</span>
                  </Button>
                </div>
              ) :

                auth?.user ? (
                  <ViewConversationDialog
                    conversationId={existingConversationId}
                    authUserId={auth.user.id}
                    speakerSlug={speaker.slug}
                    speakerName={speaker.name}
                    autoOpen={openContact}
                  />
                ) : (
                  <Button className="text-md px-3 sm:px-8" asChild>
                    <Link href={`/signup?redirect=/speakers/${speaker.slug}&contact=1`}>
                      <MessageCircle className="w-4 h-4" />
                      <span className="hidden sm:inline">Contact speaker</span>
                      <span className="sm:hidden">Contact</span>
                    </Link>
                  </Button>
                )}

                {speaker.linkedinUrl && (
                <>
                    {/* <span className="text-muted-foreground text-right max-sm:hidden mr-4">Connect with this speaker via LinkedIn to book:</span> */}
                    <Button asChild disabled className="text-md px-3 sm:px-8 bg-[#0077B5] hover:bg-[#005885] text-white hover:text-white border-[#0077B5] hover:border-[#005885] transition-colors">
                        <a href={speaker.linkedinUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                        <LinkedinIcon className="w-4 h-4" />
                            <span className="hidden sm:inline">LinkedIn profile</span>
                            <span className="sm:hidden">LinkedIn</span>
                        </a>
                    </Button>
                </>

                )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-0 py-8 md:px-6">
        <div className="space-y-8 px-6 md:px-0">
          <SpeakerHeader speaker={speaker} onOpenLightbox={() => openLightbox(mainPhotoIndex)} />
        
          <SpeakerDetails speaker={speaker} downloadBioPDF={downloadBioPDF} shareOnLinkedIn={shareOnLinkedIn} onOpenLightbox={openLightbox} headshotOffset={mainPhotoInGallery ? 0 : 1} />
          <Lightbox
            open={lightboxOpen}
            close={() => setLightboxOpen(false)}
            slides={lightboxSlides}
            index={lightboxIndex}
          />
        </div>
      </div>


      <Footer />
    </div>
</>
  );
};

export default SpeakerProfile;